import React, { useState, useEffect, useContext, useMemo } from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const selectCustomStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "10px",
    borderColor: state.isFocused ? "#4f46e5" : "#cbd5e1",
    minHeight: "42px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(99, 102, 241, 0.15)" : "none",
    "&:hover": {
      borderColor: "#4f46e5",
    },
    fontSize: "0.9rem",
    backgroundColor: "#ffffff",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#4f46e5"
      : state.isFocused
      ? "#eef2ff"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#1e293b",
    fontSize: "0.9rem",
    padding: "8px 12px",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#4338ca",
      color: "#ffffff",
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontSize: "0.9rem",
  }),
};

export default function ManageOMRTemplates() {
  const [templatesData, setTemplatesData] = useState([]);
  const [instituteOptions, setInstituteOptions] = useState([]);
  const [standardizedTestOptions, setStandardizedTestOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const { isAuthenticated, primaryRole } = useContext(AuthContext);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    { name: "Manage OMR Templates", path: "/superadmin/omr-templates", isLast: true },
  ];

  const createButtons = [
    {
      text: "Add OMR Template",
      onClick: () => {
        setFormData({
          templateId: "",
          templateName: "",
          templateFile: null,
          instituteId: null,
          standardizedTestId: null,
        });
        setFormError("");
        setIsCreateModalOpen(true);
      },
    },
  ];

  const [formData, setFormData] = useState({
    templateId: "",
    templateName: "",
    templateFile: null,
    instituteId: null,
    standardizedTestId: null,
  });

  const [editFormData, setEditFormData] = useState({
    id: "",
    templateId: "",
    templateName: "",
    templateFile: null,
    templateFileChanged: false,
    templateFileUrl: "",
    instituteId: null,
    standardizedTestId: null,
  });

  const [initialEditFormData, setInitialEditFormData] = useState({
    templateId: "",
    templateName: "",
    instituteIdValue: null,
    standardizedTestIdValue: null,
  });

  const [formError, setFormError] = useState("");

  const fetchInstitutes = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-all`,
        { method: "GET", credentials: "include" }
      );
      if (response && response.ok) {
        const data = await response.json();
        const options = (data.data || data).map((inst) => ({
          label: `${inst.name} (${inst.code})`,
          value: inst._id,
        }));
        setInstituteOptions(options);
      }
    } catch (err) {
      console.error("Failed to fetch institutes:", err);
    }
  };

  const fetchStandardizedTests = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/standardized-test/get-all`,
        { method: "GET", credentials: "include" }
      );
      if (response && response.ok) {
        const data = await response.json();
        const options = (data.data || data).map((test) => ({
          label: `${test.templateName} (${test.templateId})`,
          value: test._id,
          templateId: test.templateId,
        }));
        setStandardizedTestOptions(options);
      }
    } catch (err) {
      console.error("Failed to fetch standardized tests:", err);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${serverUrl}/api/omr-template/get-all`,
        { method: "GET", credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setTemplatesData(data.data || data);
      } else {
        throw new Error("Failed to fetch templates");
      }
    } catch (err) {
      setError(err.message);
      swalError("Error", "Failed to fetch OMR templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstitutes();
    fetchStandardizedTests();
    fetchTemplates();
  }, []);

  const handleDelete = async (id, name) => {
    const result = await swalConfirm(
      "Delete OMR Template",
      `Are you sure you want to delete "${name}"?`,
      "Yes, delete it!",
      "No, Cancel"
    );
    if (result.isConfirmed) {
      try {
        setIsDeleting(true);
        setDeletingId(id);
        const response = await fetchWithAuth(
          `${serverUrl}/api/omr-template/delete/${id}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );
        if (response.ok) {
          swalSuccess("Deleted", "Template deleted successfully");
          fetchTemplates();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete template");
        }
      } catch (err) {
        swalError("Error", err.message);
      } finally {
        setIsDeleting(false);
        setDeletingId(null);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file && file.size > 5 * 1024 * 1024) {
        swalError("Error", "File size must be less than 5MB");
        e.target.value = null;
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: file || null }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const checkEditChanges = (nextEditData) => {
    const isIdChanged =
      (nextEditData.templateId || "").trim() !==
      (initialEditFormData.templateId || "").trim();
    const isNameChanged =
      (nextEditData.templateName || "").trim() !==
      (initialEditFormData.templateName || "").trim();
    const isFileChanged = !!nextEditData.templateFileChanged;
    const isInstChanged =
      (nextEditData.instituteId?.value || null) !==
      (initialEditFormData.instituteIdValue || null);
    const isStdChanged =
      (nextEditData.standardizedTestId?.value || null) !==
      (initialEditFormData.standardizedTestIdValue || null);

    setIsEditChanged(
      isIdChanged ||
        isNameChanged ||
        isFileChanged ||
        isInstChanged ||
        isStdChanged
    );
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file && file.size > 5 * 1024 * 1024) {
        swalError("Error", "File size must be less than 5MB");
        e.target.value = null;
        return;
      }
      setEditFormData((prev) => {
        const next = {
          ...prev,
          [name]: file || null,
          templateFileChanged: true,
        };
        checkEditChanges(next);
        return next;
      });
    } else {
      setEditFormData((prev) => {
        const next = {
          ...prev,
          [name]: value,
        };
        checkEditChanges(next);
        return next;
      });
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!formData.templateId.trim()) {
      setFormError("Template ID is required");
      return;
    }
    if (!formData.templateName.trim()) {
      setFormError("Template Name is required");
      return;
    }
    if (!formData.instituteId?.value) {
      setFormError("Institute is required");
      return;
    }
    if (!formData.templateFile) {
      setFormError("Template File is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const form = new FormData();
      form.append("templateId", formData.templateId.trim());
      form.append("templateName", formData.templateName.trim());
      form.append("templateFile", formData.templateFile);
      form.append("instituteId", formData.instituteId.value);
      if (formData.standardizedTestId?.value) {
        form.append("standardizedTestId", formData.standardizedTestId.value);
      }

      const response = await fetchWithAuth(`${serverUrl}/api/omr-template/add`, {
        method: "POST",
        body: form,
      });

      if (response.ok) {
        swalSuccess("Created", "Template created successfully");
        setFormData({
          templateId: "",
          templateName: "",
          templateFile: null,
          instituteId: null,
          standardizedTestId: null,
        });
        setIsCreateModalOpen(false);
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create template");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTemplate = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!editFormData.templateId.trim()) {
      setFormError("Template ID is required");
      return;
    }
    if (!editFormData.templateName.trim()) {
      setFormError("Template Name is required");
      return;
    }
    if (!editFormData.instituteId?.value) {
      setFormError("Institute is required");
      return;
    }

    try {
      setIsSubmitting(true);

      const form = new FormData();
      form.append("templateId", editFormData.templateId.trim());
      form.append("templateName", editFormData.templateName.trim());
      form.append("instituteId", editFormData.instituteId.value);
      form.append(
        "standardizedTestId",
        editFormData.standardizedTestId
          ? editFormData.standardizedTestId.value
          : ""
      );

      if (editFormData.templateFileChanged && editFormData.templateFile) {
        form.append("templateFile", editFormData.templateFile);
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/omr-template/update/${editFormData.id}`,
        {
          method: "PUT",
          body: form,
        }
      );

      if (response.ok) {
        swalSuccess("Updated", "Template updated successfully");
        setIsEditModalOpen(false);
        fetchTemplates();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update template");
      }
    } catch (err) {
      setFormError(err.message);
      swalError("Error", err.message);
    } finally {
      setIsSubmitting(false);
      setIsEditChanged(false);
    }
  };

  const openEditModal = (template) => {
    let selectedInstitute = null;
    if (template.instituteId) {
      const instId =
        typeof template.instituteId === "object"
          ? template.instituteId._id
          : template.instituteId;
      selectedInstitute =
        instituteOptions.find((opt) => opt.value === instId) || null;
      if (!selectedInstitute && typeof template.instituteId === "object") {
        selectedInstitute = {
          value: template.instituteId._id,
          label: `${template.instituteId.name} (${template.instituteId.code})`,
        };
      }
    }

    let selectedStdTest = null;
    if (template.standardizedTestId) {
      const stdId =
        typeof template.standardizedTestId === "object"
          ? template.standardizedTestId._id
          : template.standardizedTestId;
      selectedStdTest =
        standardizedTestOptions.find((opt) => opt.value === stdId) || null;
      if (
        !selectedStdTest &&
        typeof template.standardizedTestId === "object"
      ) {
        selectedStdTest = {
          value: template.standardizedTestId._id,
          label: `${template.standardizedTestId.templateName || "Template"} (${
            template.standardizedTestId.templateId || ""
          })`,
        };
      }
    }

    setInitialEditFormData({
      templateId: template.templateId || "",
      templateName: template.templateName || "",
      instituteIdValue: selectedInstitute ? selectedInstitute.value : null,
      standardizedTestIdValue: selectedStdTest ? selectedStdTest.value : null,
    });

    setEditFormData({
      id: template._id,
      templateId: template.templateId || "",
      templateName: template.templateName || "",
      templateFileUrl: template.templateURL || template.templateFileUrl || "",
      templateFile: null,
      templateFileChanged: false,
      instituteId: selectedInstitute,
      standardizedTestId: selectedStdTest,
    });
    setIsEditChanged(false);
    setFormError("");
    setIsEditModalOpen(true);
  };

  const copyToClipboard = (text, id) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Formatted data items for Table component so built-in filter and search work seamlessly
  const formattedTemplatesData = useMemo(() => {
    return templatesData.map((item) => {
      const instituteName = item.instituteId?.name
        ? `${item.instituteId.name} (${item.instituteId.code || ""})`
        : "General (All Institutes)";
      const standardizedTestName =
        item.standardizedTestId?.templateName ||
        item.standardizedTestId?.templateId ||
        (typeof item.standardizedTestId === "string"
          ? item.standardizedTestId
          : "Not Linked");

      return {
        ...item,
        instituteName,
        standardizedTestName,
      };
    });
  }, [templatesData]);

  const columns = [
    {
      key: "templateId",
      label: "Template ID",
      sortable: true,
      filter: true,
      render: (row) => (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              backgroundColor: "#f1f5f9",
              color: "#334155",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "0.85rem",
              fontWeight: "600",
              border: "1px solid #e2e8f0",
              letterSpacing: "0.02em",
            }}
          >
            {row.templateId}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(row.templateId, row._id);
            }}
            title={copiedId === row._id ? "Copied!" : "Copy Template ID"}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px",
              color: copiedId === row._id ? "#16a34a" : "#94a3b8",
              fontSize: "0.8rem",
              transition: "color 0.15s ease",
            }}
          >
            <i
              className={`fa-solid ${
                copiedId === row._id ? "fa-check" : "fa-copy"
              }`}
            ></i>
          </button>
        </div>
      ),
    },
    {
      key: "templateName",
      label: "Template Name",
      sortable: true,
      filter: true,
      render: (row) => (
        <span style={{ fontWeight: "600", color: "#1e293b" }}>
          {row.templateName}
        </span>
      ),
    },
    {
      key: "instituteName",
      label: "Institute",
      sortable: true,
      filter: true,
      render: (row) => {
        if (row.instituteId?.name) {
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                padding: "3px 10px",
                borderRadius: "20px",
                fontSize: "0.8rem",
                fontWeight: "500",
                backgroundColor: "#eff6ff",
                color: "#1d4ed8",
                border: "1px solid #bfdbfe",
              }}
            >
              <i className="fa-solid fa-building-columns" style={{ fontSize: "0.75rem" }}></i>
              {row.instituteName}
            </span>
          );
        }
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "3px 10px",
              borderRadius: "20px",
              fontSize: "0.8rem",
              fontWeight: "500",
              backgroundColor: "#f8fafc",
              color: "#64748b",
              border: "1px solid #e2e8f0",
            }}
          >
            <i className="fa-solid fa-globe" style={{ fontSize: "0.75rem" }}></i>
            General (All Institutes)
          </span>
        );
      },
    },
    {
      key: "standardizedTestName",
      label: "Grading Template",
      sortable: true,
      filter: true,
      render: (row) => {
        if (row.standardizedTestId) {
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                padding: "3px 10px",
                borderRadius: "20px",
                fontSize: "0.8rem",
                fontWeight: "500",
                backgroundColor: "#f5f3ff",
                color: "#6d28d9",
                border: "1px solid #ddd6fe",
              }}
              title="Grading template auto-selected during test creation"
            >
              <i className="fa-solid fa-graduation-cap" style={{ fontSize: "0.75rem" }}></i>
              {row.standardizedTestName}
            </span>
          );
        }
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "3px 8px",
              borderRadius: "20px",
              fontSize: "0.75rem",
              color: "#94a3b8",
              backgroundColor: "#f8fafc",
              border: "1px dashed #cbd5e1",
            }}
          >
            <i className="fa-solid fa-link-slash" style={{ fontSize: "0.7rem" }}></i>
            Not Linked
          </span>
        );
      },
    },
    {
      key: "templateFileUrl",
      label: "OMR File",
      sortable: false,
      render: (row) => {
        const fileUrl = row.templateURL || row.templateFileUrl;
        return (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="view-btn"
            title="View OMR Sheet"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
            }}
          >
            <i className="fa-solid fa-eye"></i>
          </a>
        );
      },
    },
    {
      key: "edit",
      label: "Edit",
      sortable: false,
      render: (row) => (
        <button
          className="edit-btn"
          onClick={() => openEditModal(row)}
          title="Edit Template"
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
    },
    {
      key: "delete",
      label: "Delete",
      sortable: false,
      render: (row) => (
        <button
          className="delete-btn"
          onClick={() => handleDelete(row._id, row.templateName)}
          disabled={isDeleting && deletingId === row._id}
          title="Delete Template"
        >
          {isDeleting && deletingId === row._id ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash"></i>
          )}
        </button>
      ),
    },
  ];

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/" />;
  }

  return (
    <div>
      <Navbar breadcrumbs={breadcrumbs} createButtons={createButtons} />

      <div className="container" style={{ marginTop: "24px", marginBottom: "40px" }}>
        {/* Content Table / Empty States */}
        {loading ? (
          <Loader />
        ) : error && templatesData.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              border: "1px solid #fee2e2",
            }}
          >
            <h3 style={{ color: "#ef4444", marginBottom: "8px" }}>
              Error Loading Templates
            </h3>
            <p style={{ color: "#64748b", marginBottom: "16px" }}>{error}</p>
            <button
              onClick={() => fetchTemplates()}
              className="btn-submit"
              style={{ width: "auto", padding: "8px 24px" }}
            >
              Try Again
            </button>
          </div>
        ) : formattedTemplatesData.length > 0 ? (
          <Table
            columns={columns}
            data={formattedTemplatesData}
            caption="OMR Templates"
          />
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "48px 20px",
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              border: "1px dashed #cbd5e1",
            }}
          >
            <div
              style={{
                width: "54px",
                height: "54px",
                borderRadius: "50%",
                backgroundColor: "#eef2ff",
                color: "#4f46e5",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                marginBottom: "14px",
              }}
            >
              <i className="fa-solid fa-sheet-plastic"></i>
            </div>
            <h3 style={{ margin: "0 0 8px", color: "#1e293b" }}>
              No OMR templates found
            </h3>
            <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: "0.9rem" }}>
              Create your first OMR template to get started with automatic grading.
            </p>
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              style={{
                backgroundColor: "#4f46e5",
                color: "#ffffff",
                border: "none",
                padding: "10px 22px",
                borderRadius: "8px",
                fontSize: "0.9rem",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)",
              }}
            >
              <i className="fa-solid fa-plus" style={{ marginRight: "6px" }}></i>
              Add OMR Template
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add OMR Template"
      >
        <form onSubmit={handleCreateTemplate}>
          {formError && <div className="error-message">{formError}</div>}

          {/* Institute Selection */}
          <div className="form-group">
            <label>Institute *</label>
            <Select
              options={instituteOptions}
              value={formData.instituteId}
              onChange={(selected) =>
                setFormData((prev) => ({ ...prev, instituteId: selected }))
              }
              placeholder="Select institute..."
              styles={selectCustomStyles}
            />
            <small
              style={{
                display: "block",
                marginTop: "4px",
                fontSize: "0.78rem",
                color: "#64748b",
              }}
            >
              Assign to a specific institute for dedicated access.
            </small>
          </div>

          {/* Template ID */}
          <div className="form-group">
            <label>Template ID *</label>
            <input
              type="text"
              name="templateId"
              value={formData.templateId}
              onChange={handleInputChange}
              placeholder="e.g. omr-standard-100-v1"
              required
            />
            <small
              style={{
                display: "block",
                marginTop: "4px",
                fontSize: "0.78rem",
                color: "#64748b",
              }}
            >
              Unique identifier used across the evaluation engine.
            </small>
          </div>

          {/* Template Name */}
          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              name="templateName"
              value={formData.templateName}
              onChange={handleInputChange}
              placeholder="e.g. 100 Questions Standard OMR (4 Options)"
              required
            />
          </div>

          {/* Standardized Test / Grading Template Selection */}
          <div className="form-group">
            <label>
              Grading Template (Standardized Test){" "}
              <span
                style={{
                  fontSize: "0.72rem",
                  fontWeight: "normal",
                  color: "#64748b",
                  marginLeft: "4px",
                }}
              >
                (Optional)
              </span>
            </label>
            <Select
              options={standardizedTestOptions}
              value={formData.standardizedTestId}
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  standardizedTestId: selected,
                }))
              }
              placeholder="Select grading template to link..."
              isClearable
              styles={selectCustomStyles}
            />
            <div
              style={{
                marginTop: "6px",
                padding: "8px 12px",
                backgroundColor: "#f8fafc",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "0.78rem",
                color: "#475569",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <i
                className="fa-solid fa-circle-info"
                style={{ color: "#4f46e5", marginTop: "2px" }}
              ></i>
              <span>
                <strong>Auto-Select:</strong> Linking a grading template allows the system to auto-select the grading template whenever teachers choose this OMR sheet, eliminating the need to pick both dropdowns.
              </span>
            </div>
          </div>

          {/* Template File */}
          <div className="form-group">
            <label>Template File (PDF/Image) *</label>
            <input
              type="file"
              name="templateFile"
              accept="image/*,application/pdf"
              onChange={handleInputChange}
              required
            />
            <small
              style={{
                display: "block",
                marginTop: "4px",
                fontSize: "0.78rem",
                color: "#64748b",
              }}
            >
              Upload PDF or High-Resolution Image (Max 5MB).
            </small>
          </div>

          <div className="modal-buttons" style={{ marginTop: "24px" }}>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader style={{ left: 0, top: 0, color: "#fff" }} />
              ) : (
                "Create Template"
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit OMR Template"
      >
        <form onSubmit={handleEditTemplate}>
          {formError && <div className="error-message">{formError}</div>}

          {/* Institute Selection */}
          <div className="form-group">
            <label>Institute *</label>
            <Select
              options={instituteOptions}
              value={editFormData.instituteId}
              onChange={(selected) => {
                setEditFormData((prev) => {
                  const next = { ...prev, instituteId: selected };
                  checkEditChanges(next);
                  return next;
                });
              }}
              placeholder="Select institute..."
              styles={selectCustomStyles}
            />
            <small
              style={{
                display: "block",
                marginTop: "4px",
                fontSize: "0.78rem",
                color: "#64748b",
              }}
            >
              Assign to a specific institute.
            </small>
          </div>

          {/* Template ID */}
          <div className="form-group">
            <label>Template ID *</label>
            <input
              type="text"
              name="templateId"
              value={editFormData.templateId}
              onChange={handleEditInputChange}
              placeholder="Enter unique template ID"
              required
            />
          </div>

          {/* Template Name */}
          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              name="templateName"
              value={editFormData.templateName}
              onChange={handleEditInputChange}
              placeholder="Enter template name"
              required
            />
          </div>

          {/* Standardized Test / Grading Template Selection */}
          <div className="form-group">
            <label>
              Grading Template (Standardized Test){" "}
              <span
                style={{
                  fontSize: "0.72rem",
                  fontWeight: "normal",
                  color: "#64748b",
                  marginLeft: "4px",
                }}
              >
                (Optional)
              </span>
            </label>
            <Select
              options={standardizedTestOptions}
              value={editFormData.standardizedTestId}
              onChange={(selected) => {
                setEditFormData((prev) => {
                  const next = { ...prev, standardizedTestId: selected };
                  checkEditChanges(next);
                  return next;
                });
              }}
              placeholder="Select grading template to link..."
              isClearable
              styles={selectCustomStyles}
            />
            <div
              style={{
                marginTop: "6px",
                padding: "8px 12px",
                backgroundColor: "#f8fafc",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "0.78rem",
                color: "#475569",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <i
                className="fa-solid fa-circle-info"
                style={{ color: "#4f46e5", marginTop: "2px" }}
              ></i>
              <span>
                <strong>Auto-Select:</strong> When linked, selecting this OMR during exam creation will automatically select this grading template.
              </span>
            </div>
          </div>

          {/* Update Template File */}
          <div className="form-group">
            <label>Update Template File (Optional)</label>
            <input
              type="file"
              name="templateFile"
              accept="image/*,application/pdf"
              onChange={handleEditInputChange}
            />
            {editFormData.templateFileUrl && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "10px 14px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span
                  style={{
                    fontSize: "0.82rem",
                    color: "#475569",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <i
                    className="fa-solid fa-file-pdf"
                    style={{ color: "#ef4444" }}
                  ></i>
                  {editFormData.templateFileChanged
                    ? "New file selected"
                    : "Current file on record"}
                </span>
                <a
                  href={editFormData.templateFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "0.82rem",
                    fontWeight: "600",
                    color: "#4f46e5",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <i className="fa-solid fa-arrow-up-right-from-square"></i>
                  View File
                </a>
              </div>
            )}
          </div>

          <div className="modal-buttons" style={{ marginTop: "24px" }}>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || !isEditChanged}
            >
              {isSubmitting ? (
                <Loader style={{ left: 0, top: 0, color: "#fff" }} />
              ) : (
                "Update Template"
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
